import { useAppSelector } from "@src/ducks/ducksHook";
import useSoundService from "@src/ducks/hooks/useSoundService";
import { selectedLobbySuccess } from "@src/ducks/slices/lobby.slice";
import { selectedGameEntered, selectedUserCoins } from "@src/ducks/slices/user.slice";
import numeral from "numeral";
import React from "react";
import { Text } from "react-native";
import { useCoinContext } from "../../main/CoinProvider";
import useStyles from "./styles.css";

const SilverCoins = ({ baseHeight }: { baseHeight: number }) => {
  const styles = useStyles();
  const { claimSoundToggle } = useSoundService();
  const { changeAnimationStatus } = useCoinContext();
  const { SILVER } = useAppSelector(selectedUserCoins);
  const lobbySuccess = useAppSelector(selectedLobbySuccess);
  const hasInGame = useAppSelector(selectedGameEntered);

  const [currentSilver, setCurrentSilver] = React.useState(0);

  const animationRef = React.useRef<number | null>(null);
  const startTimeRef = React.useRef<number | null>(null);
  const startValueRef = React.useRef(0);
  const targetValueRef = React.useRef(0);

  const clearAnimation = React.useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      startTimeRef.current = null;
    }
    changeAnimationStatus(false); // ✅ always close animation state
  }, []);

  const animateTo = React.useCallback(
    (target: number, duration = 900) => {
      clearAnimation();

      const startValue = currentSilver;
      startValueRef.current = startValue;
      targetValueRef.current = target;
      startTimeRef.current = null;

      const delta = target - startValue;

      if (!lobbySuccess) {
        setCurrentSilver(target);
        return;
      }

      if (hasInGame) {
        setCurrentSilver(target);
        return;
      }

      if (delta > 0) {
        changeAnimationStatus(true);
        claimSoundToggle();
      }

      const animate = (timestamp: number) => {
        if (!startTimeRef.current) startTimeRef.current = timestamp;
        const elapsed = timestamp - startTimeRef.current;

        const progress = Math.min(elapsed / duration, 1);
        const newValue = startValue + delta * progress;

        setCurrentSilver(newValue);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          clearAnimation();
          changeAnimationStatus(false);
          setCurrentSilver(target);
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    },
    [currentSilver, hasInGame, lobbySuccess, claimSoundToggle, changeAnimationStatus, clearAnimation]
  );

  React.useEffect(() => {
    animateTo(SILVER);
    return clearAnimation;
  }, [SILVER]);

  return (
    <Text style={[styles.silverText, { fontSize: baseHeight * 0.16 }]}>
      {numeral(currentSilver).format("0,000")}
    </Text>
  );
};

export default SilverCoins;
